import { useQuery } from '@tanstack/react-query'
import React, { useEffect } from 'react'
import { useFormContext, type Control } from 'react-hook-form'
import { Box, Grid, Typography } from '@mui/material'
import FormSwitch from '../../../components/FormElements/FormSwitch'
import FormInput from '../../../components/FormElements/FormInput'
import FormAutoComplete from '../../../components/FormElements/FormAutoComplete'
import { conditions } from './constant'
import { SlotStatus } from '../../../models/DistributionPlan'
import { getSlotData } from '../../../store/slices/distributionPlanSlice/selectors'
import { AutoImportManager } from '../../../APIManager'

interface FormSectionProps {
  fields: any
  index: number
  control: Control
  disabled: boolean

}

const FormSection: React.FC<FormSectionProps> = ({
  fields,
  index,
  control,
  disabled
}) => {
  const { watch, setValue } = useFormContext()

  const condition = watch(`surveyDatesFilters[${index}].condition`)
  const disabledNumOfDays = condition === 'on'
  const slot = getSlotData()

  useEffect(() => {
    if (condition === 'on') {
      setValue(`surveyDatesFilters[${index}].noOfDays`, 0)
    }
  }, [condition, index, control])

  const { surveyDateType, subTitle } = fields[index]
  const enabled = watch(`surveyDatesFilters[${index}].enabled`)

  const { data: sectionFieldTypeList, isLoading } = useQuery({
    queryKey: ['dates'],
    retry: false,
    staleTime: 5000,
    queryFn: async (): Promise<string[]> => {
      const result = await AutoImportManager.getSectionFields()
      return result
    }
  })
  const slotStatus = slot?.slotNodeDetails?.slotStatus
  const isButtonDisabled = slotStatus !== SlotStatus.Draft
  return (
    <>
      <Box mt={5} display="flex" alignItems="center">
        {/* Show Switch only for release Dates */}
        {index > 1 &&
          <FormSwitch
            name={`surveyDatesFilters[${index}].enabled`}
            control={control}
            disabled={disabled}
            checked={watch(`surveyDatesFilters[${index}].enabled`)}
          />
        }
        <Typography fontWeight={600} color={'#000'}>
          {surveyDateType}{enabled && '*'}
        </Typography>
      </Box>
      <Typography color={'#000'} variant="body2" mt={1}>
        {subTitle}
      </Typography>
      <Grid container spacing={2} mt={2}>
        <Grid item xs={12} sm={4} md={3}>
          <FormInput
            name={`surveyDatesFilters[${index}].noOfDays`}
            label="Days"
            placeholder="Days"
            disabled={disabledNumOfDays || !enabled || isButtonDisabled }
            required={enabled}
            type='number'
          />
        </Grid>
        <Grid item xs={12} sm={4} md={4}>
          <FormAutoComplete
            name={`surveyDatesFilters[${index}].condition`}
            options={conditions}
            placeholder="Condition"
            label="Condition"
            getOptionLabel={(option: { title: string, value: string | number }) => option?.title}
            getOptionValue={(option: { title: string, value: string | number }) => option?.value}
            readOnly={!enabled || isButtonDisabled}
            required={enabled}
          />
        </Grid>
        <Grid item xs={12} sm={4} md={5}>
          <FormAutoComplete
            name={`surveyDatesFilters[${index}].sectionFieldType`}
            options={sectionFieldTypeList}
            placeholder="Academic Date"
            label="Academic Date"
            getOptionLabel={(option: string) => option}
            getOptionValue={(option: string) => option}
            loading={isLoading}
            readOnly={!enabled || isButtonDisabled}
            required={enabled}
          />
        </Grid>
      </Grid>
    </>
  )
}

export default FormSection
